/**
 * Soft loader for `curva-tess` Wasm. Missing pkg → `undefined` (TS kernels stay default).
 *
 * Pkgs are ensured by `bun run ensure:wasm` (wired into `dev` / `dev:desktop`)
 * and by the production `build`. Opt out with `CURVA_WASM_STROKES=0` / `CURVA_WASM_MESH=0`.
 */

import type { StrokeAlign } from "@/document/model/styles";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import {
  handleCoord,
  resetCurveKernelBackendForTests,
  setCurveKernelBackend,
  unpackFlatPoints,
  type CurveKernelBackend,
  type Point as CurvePoint,
} from "@/document/operations/curveKernels";
import {
  resetRegionContourBackendForTests,
  setRegionContourBackend,
  type CurveStepSource,
  type SampledVectorRegion,
} from "@/document/operations/vectorGeometry";
import type { Point } from "@/document/operations/pathGeometry";
import {
  CURVA_TESS_WASM_PUBLIC_PREFIX,
  shouldLoadWasmPkgOverHttp,
} from "@/wasmPkgPaths";
import {
  resetMeshKernelBackendForTests,
  setMeshKernelBackend,
  type MeshKernelBackend,
  type PackedMesh,
} from "./meshKernels";

export interface CurvaTessWasm {
  version: string;
  smokeTriangle: () => Float32Array;
  strokePolylineToTriangles: (
    points: Float32Array | number[],
    strokeWidth: number,
    closed: boolean,
    align: StrokeAlign,
  ) => Float32Array;
  networkToStrokeTriangles: (
    network: VectorNetwork,
    strokeWidth: number,
    align: StrokeAlign,
  ) => Float32Array;
  /** Present when the Wasm pkg exports the packed binary API. */
  networkToStrokeTrianglesPacked?: (
    network: VectorNetwork,
    strokeWidth: number,
    align: StrokeAlign,
  ) => Float32Array;
  triangulateEvenOddContours: (contours: Point[][]) => Float32Array;
  /** Present when the Wasm pkg exports the packed binary API. */
  triangulateEvenOddContoursPacked?: (contours: Point[][]) => Float32Array;
  /** Present when the Wasm pkg exports the packed region walk API. */
  sampleNetworkRegionContoursPacked?: (
    network: VectorNetwork,
    steps: CurveStepSource,
  ) => SampledVectorRegion[];
  sampleEdgeBezier: (
    from: CurvePoint,
    to: CurvePoint,
    fromHandle: CurvePoint | undefined,
    toHandle: CurvePoint | undefined,
    steps: number,
  ) => Float32Array;
  adaptiveCurveSteps: (
    from: CurvePoint,
    to: CurvePoint,
    fromHandle: CurvePoint | undefined,
    toHandle: CurvePoint | undefined,
  ) => number;
  signedArea: (points: Point[]) => number;
  normalizeNestedContourWindings: (contours: Point[][]) => Point[][];
}

type CurvaTessPkg = {
  default: (module_or_path?: unknown) => Promise<unknown>;
  curvaTessVersion: () => string;
  smokeTriangle: () => Float32Array;
  strokePolylineToTriangles: (
    points: Float32Array,
    strokeWidth: number,
    closed: boolean,
    align: string,
  ) => Float32Array;
  networkToStrokeTriangles: (
    networkJson: string,
    strokeWidth: number,
    align: string,
  ) => Float32Array;
  networkToStrokeTrianglesPacked?: (
    vertices: Float32Array,
    edges: Float32Array,
    strokeWidth: number,
    align: string,
  ) => Float32Array;
  triangulateEvenOddContours: (contoursJson: string) => Float32Array;
  triangulateEvenOddContoursPacked?: (counts: Uint32Array, xy: Float32Array) => Float32Array;
  sampleNetworkRegionContoursPacked?: (
    vertices: Float32Array,
    edges: Float32Array,
    edgeSteps: Uint32Array,
  ) => PackedSampledRegionsPkg;
  sampleEdgeBezier: (
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    fromHX: number,
    fromHY: number,
    toHX: number,
    toHY: number,
    steps: number,
  ) => Float32Array;
  adaptiveCurveSteps: (
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    fromHX: number,
    fromHY: number,
    toHX: number,
    toHY: number,
  ) => number;
  signedArea: (points: Float32Array) => number;
  normalizeNestedContourWindings: (contoursJson: string) => string;
};

type PackedSampledRegionsPkg = {
  region_edge_counts: Uint32Array;
  region_edges: Uint32Array;
  region_point_counts: Uint32Array;
  xy: Float32Array;
  free?: () => void;
};

const FILE_PKG_JS = new URL("./wasm/pkg/curva_tess.js", import.meta.url).href;
const FILE_PKG_WASM = new URL("./wasm/pkg/curva_tess_bg.wasm", import.meta.url);

function pkgJsHref(): string {
  return shouldLoadWasmPkgOverHttp()
    ? `${CURVA_TESS_WASM_PUBLIC_PREFIX}curva_tess.js`
    : FILE_PKG_JS;
}

function pkgWasmUrl(): URL {
  return shouldLoadWasmPkgOverHttp()
    ? new URL(`${CURVA_TESS_WASM_PUBLIC_PREFIX}curva_tess_bg.wasm`, globalThis.location?.href ?? "http://localhost/")
    : FILE_PKG_WASM;
}

let loadPromise: Promise<CurvaTessWasm | undefined> | undefined;
let wasmMeshEnabled = false;

async function importPkg(): Promise<CurvaTessPkg | undefined> {
  try {
    return (await import(pkgJsHref())) as CurvaTessPkg;
  } catch {
    return undefined;
  }
}

async function wasmInitInput(): Promise<BufferSource | URL> {
  const wasmUrl = pkgWasmUrl();
  if (typeof Bun !== "undefined" && wasmUrl.protocol === "file:") {
    return await Bun.file(wasmUrl).arrayBuffer();
  }
  return wasmUrl;
}

function packPoints(points: Array<{ x: number; y: number }>): Float32Array {
  const packed = new Float32Array(points.length * 2);
  for (let index = 0; index < points.length; index++) {
    packed[index * 2] = points[index]!.x;
    packed[index * 2 + 1] = points[index]!.y;
  }
  return packed;
}

const STROKE_JOIN_CODE: Record<string, number> = {
  miter: 0,
  round: 1,
  bevel: 2,
};

/**
 * Pack a VectorNetwork into flat Float32 buffers for `networkToStrokeTrianglesPacked`.
 *
 * **vertices** stride 4: `[x, y, cornerRadius, strokeJoin]` (0=miter, 1=round, 2=bevel).
 * **edges** stride 8: `[fromIdx, toIdx, fromHX|NaN, fromHY|NaN, toHX|NaN, toHY|NaN, 0, 0]`.
 */
export function packNetworkForWasm(network: VectorNetwork): {
  vertices: Float32Array;
  edges: Float32Array;
} {
  const idToIndex = new Map<string, number>();
  const vertices = new Float32Array(network.vertices.length * 4);
  for (let i = 0; i < network.vertices.length; i++) {
    const v = network.vertices[i]!;
    idToIndex.set(v.id, i);
    const base = i * 4;
    vertices[base] = v.x;
    vertices[base + 1] = v.y;
    vertices[base + 2] = v.cornerRadius ?? 0;
    vertices[base + 3] = STROKE_JOIN_CODE[v.strokeJoin ?? "miter"] ?? 0;
  }

  const edges = new Float32Array(network.edges.length * 8);
  for (let i = 0; i < network.edges.length; i++) {
    const e = network.edges[i]!;
    const base = i * 8;
    edges[base] = idToIndex.get(e.from.vertexId) ?? 0;
    edges[base + 1] = idToIndex.get(e.to.vertexId) ?? 0;
    if (e.from.handle) {
      edges[base + 2] = e.from.handle.x;
      edges[base + 3] = e.from.handle.y;
    } else {
      edges[base + 2] = Number.NaN;
      edges[base + 3] = Number.NaN;
    }
    if (e.to.handle) {
      edges[base + 4] = e.to.handle.x;
      edges[base + 5] = e.to.handle.y;
    } else {
      edges[base + 4] = Number.NaN;
      edges[base + 5] = Number.NaN;
    }
    edges[base + 6] = 0;
    edges[base + 7] = 0;
  }

  return { vertices, edges };
}

/** Minimal DTO matching the Rust `NetworkDto` (no fillRule/regions). Kept for tests/compat. */
export function serializeNetworkForWasm(network: VectorNetwork): string {
  return JSON.stringify({
    vertices: network.vertices.map(vertex => ({
      id: vertex.id,
      x: vertex.x,
      y: vertex.y,
      ...(vertex.cornerRadius !== undefined ? { cornerRadius: vertex.cornerRadius } : {}),
      ...(vertex.strokeJoin !== undefined ? { strokeJoin: vertex.strokeJoin } : {}),
    })),
    edges: network.edges.map(edge => ({
      id: edge.id,
      from: {
        vertexId: edge.from.vertexId,
        ...(edge.from.handle ? { handle: { x: edge.from.handle.x, y: edge.from.handle.y } } : {}),
      },
      to: {
        vertexId: edge.to.vertexId,
        ...(edge.to.handle ? { handle: { x: edge.to.handle.x, y: edge.to.handle.y } } : {}),
      },
    })),
  });
}

export function serializeContoursForWasm(contours: Point[][]): string {
  return JSON.stringify(contours.map(contour => contour.map(point => ({ x: point.x, y: point.y }))));
}

/** Pack contours into counts + flat xy for `triangulateEvenOddContoursPacked`. */
export function packContoursForWasm(contours: Point[][]): { counts: Uint32Array; xy: Float32Array } {
  let totalPoints = 0;
  for (const contour of contours) {
    totalPoints += contour.length;
  }
  const counts = new Uint32Array(contours.length);
  const xy = new Float32Array(totalPoints * 2);
  let offset = 0;
  for (let index = 0; index < contours.length; index++) {
    const contour = contours[index]!;
    counts[index] = contour.length;
    for (const point of contour) {
      xy[offset] = point.x;
      xy[offset + 1] = point.y;
      offset += 2;
    }
  }
  return { counts, xy };
}

function buildEdgeSteps(network: VectorNetwork, steps: CurveStepSource): Uint32Array {
  const edgeSteps = new Uint32Array(network.edges.length);
  for (let index = 0; index < network.edges.length; index++) {
    const edge = network.edges[index]!;
    const value = typeof steps === "number" ? steps : steps(edge);
    edgeSteps[index] = Math.max(1, Math.floor(value));
  }
  return edgeSteps;
}

function unpackSampledRegions(
  packed: PackedSampledRegionsPkg,
  network: VectorNetwork,
): SampledVectorRegion[] {
  const regions: SampledVectorRegion[] = [];
  let edgeOffset = 0;
  let pointOffset = 0;

  for (let regionIndex = 0; regionIndex < packed.region_edge_counts.length; regionIndex++) {
    const edgeCount = packed.region_edge_counts[regionIndex]!;
    const pointCount = packed.region_point_counts[regionIndex]!;
    const edgeIds: string[] = [];
    const edgeRefs: Array<{ edgeId: string; forward: boolean }> = [];

    for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
      const networkEdgeIndex = packed.region_edges[edgeOffset]!;
      const forward = packed.region_edges[edgeOffset + 1]! === 1;
      edgeOffset += 2;
      const edge = network.edges[networkEdgeIndex]!;
      edgeIds.push(edge.id);
      edgeRefs.push({ edgeId: edge.id, forward });
    }

    const points: Point[] = [];
    for (let pointIndex = 0; pointIndex < pointCount; pointIndex++) {
      points.push({
        x: packed.xy[pointOffset]!,
        y: packed.xy[pointOffset + 1]!,
      });
      pointOffset += 2;
    }

    regions.push({ edgeIds, edgeRefs, points });
  }

  return regions;
}

export function ensureCurvaTessWasm(): Promise<CurvaTessWasm | undefined> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const pkg = await importPkg();
      if (!pkg) {
        return undefined;
      }
      try {
        await pkg.default({ module_or_path: await wasmInitInput() });
        const hasPacked = typeof pkg.networkToStrokeTrianglesPacked === "function";
        const hasPackedFill = typeof pkg.triangulateEvenOddContoursPacked === "function";
        const hasPackedRegions = typeof pkg.sampleNetworkRegionContoursPacked === "function";
        return {
          version: pkg.curvaTessVersion(),
          smokeTriangle: () => pkg.smokeTriangle(),
          strokePolylineToTriangles: (points, strokeWidth, closed, align) => {
            const flat = points instanceof Float32Array ? points : Float32Array.from(points);
            return pkg.strokePolylineToTriangles(flat, strokeWidth, closed, align);
          },
          networkToStrokeTriangles: (network, strokeWidth, align) =>
            pkg.networkToStrokeTriangles(serializeNetworkForWasm(network), strokeWidth, align),
          ...(hasPacked
            ? {
                networkToStrokeTrianglesPacked: (
                  network: VectorNetwork,
                  strokeWidth: number,
                  align: StrokeAlign,
                ) => {
                  const packed = packNetworkForWasm(network);
                  return pkg.networkToStrokeTrianglesPacked!(
                    packed.vertices,
                    packed.edges,
                    strokeWidth,
                    align,
                  );
                },
              }
            : {}),
          ...(hasPackedRegions
            ? {
                sampleNetworkRegionContoursPacked: (
                  network: VectorNetwork,
                  steps: CurveStepSource,
                ) => {
                  const packedNetwork = packNetworkForWasm(network);
                  const edgeSteps = buildEdgeSteps(network, steps);
                  const result = pkg.sampleNetworkRegionContoursPacked!(
                    packedNetwork.vertices,
                    packedNetwork.edges,
                    edgeSteps,
                  );
                  try {
                    return unpackSampledRegions(result, network);
                  } finally {
                    result.free?.();
                  }
                },
              }
            : {}),
          triangulateEvenOddContours: contours =>
            pkg.triangulateEvenOddContours(serializeContoursForWasm(contours)),
          ...(hasPackedFill
            ? {
                triangulateEvenOddContoursPacked: (contours: Point[][]) => {
                  const packed = packContoursForWasm(contours);
                  return pkg.triangulateEvenOddContoursPacked!(packed.counts, packed.xy);
                },
              }
            : {}),
          sampleEdgeBezier: (from, to, fromHandle, toHandle, steps) =>
            pkg.sampleEdgeBezier(
              from.x,
              from.y,
              to.x,
              to.y,
              handleCoord(fromHandle, "x"),
              handleCoord(fromHandle, "y"),
              handleCoord(toHandle, "x"),
              handleCoord(toHandle, "y"),
              steps,
            ),
          adaptiveCurveSteps: (from, to, fromHandle, toHandle) =>
            pkg.adaptiveCurveSteps(
              from.x,
              from.y,
              to.x,
              to.y,
              handleCoord(fromHandle, "x"),
              handleCoord(fromHandle, "y"),
              handleCoord(toHandle, "x"),
              handleCoord(toHandle, "y"),
            ),
          signedArea: points => {
            const flat = new Float32Array(points.length * 2);
            for (let index = 0; index < points.length; index++) {
              flat[index * 2] = points[index]!.x;
              flat[index * 2 + 1] = points[index]!.y;
            }
            return pkg.signedArea(flat);
          },
          normalizeNestedContourWindings: contours =>
            JSON.parse(pkg.normalizeNestedContourWindings(serializeContoursForWasm(contours))) as Point[][],
        };
      } catch (error) {
        console.warn("[curva-tess] Wasm init failed; keeping TS mesh kernels", error);
        return undefined;
      }
    })().then(result => {
      // Allow retry after a missing/unbuilt pkg is produced mid-session.
      if (!result) {
        loadPromise = undefined;
      }
      return result;
    });
  }
  return loadPromise;
}

function wasmRegionBackend(tess: CurvaTessWasm): (network: VectorNetwork, steps: CurveStepSource) => SampledVectorRegion[] {
  if (typeof tess.sampleNetworkRegionContoursPacked !== "function") {
    throw new Error("sampleNetworkRegionContoursPacked missing from Wasm pkg");
  }
  return (network, steps) => tess.sampleNetworkRegionContoursPacked!(network, steps);
}

function wasmMeshBackend(tess: CurvaTessWasm): MeshKernelBackend {
  const networkStroke =
    typeof tess.networkToStrokeTrianglesPacked === "function"
      ? (network: VectorNetwork, strokeWidth: number, align: StrokeAlign) =>
          tess.networkToStrokeTrianglesPacked!(network, strokeWidth, align)
      : (network: VectorNetwork, strokeWidth: number, align: StrokeAlign) =>
          tess.networkToStrokeTriangles(network, strokeWidth, align);

  return {
    strokePolylineToTriangles(points, strokeWidth, closed, align = "inside"): PackedMesh {
      return tess.strokePolylineToTriangles(packPoints(points), strokeWidth, closed, align);
    },
    networkToStrokeTriangles(network, strokeWidth, align = "center") {
      return networkStroke(network, strokeWidth, align);
    },
    networkToStrokeTriangleChunks(network, strokeWidth, align = "center") {
      const mesh = networkStroke(network, strokeWidth, align);
      return mesh.length > 0 ? [mesh] : [];
    },
    triangulateEvenOddContours(contours) {
      return typeof tess.triangulateEvenOddContoursPacked === "function"
        ? tess.triangulateEvenOddContoursPacked(contours)
        : tess.triangulateEvenOddContours(contours);
    },
  };
}

function wasmCurveBackend(tess: CurvaTessWasm): CurveKernelBackend {
  return {
    sampleEdgeBezier(from, to, fromHandle, toHandle, steps) {
      return unpackFlatPoints(tess.sampleEdgeBezier(from, to, fromHandle, toHandle, steps));
    },
    adaptiveCurveSteps(from, to, fromHandle, toHandle) {
      return tess.adaptiveCurveSteps(from, to, fromHandle, toHandle);
    },
  };
}

function envAllowsWasmMesh(): boolean {
  const value = typeof Bun !== "undefined"
    ? (Bun.env.CURVA_WASM_STROKES ?? Bun.env.CURVA_WASM_MESH)
    : undefined;
  // Explicit opt-out only. Browser loads the pkg over `/wasm/curva-tess/`.
  if (value === "0" || value === "false") {
    return false;
  }
  return true;
}

/** Enable Wasm strokes + even-odd fills + curve sampling when the pkg loads. */
export async function enableWasmMeshKernels(options?: { force?: boolean }): Promise<boolean> {
  if (!options?.force && !envAllowsWasmMesh()) {
    wasmMeshEnabled = false;
    resetMeshKernelBackendForTests();
    resetCurveKernelBackendForTests();
    resetRegionContourBackendForTests();
    return false;
  }

  const tess = await ensureCurvaTessWasm();
  if (
    !tess
    || typeof tess.strokePolylineToTriangles !== "function"
    || typeof tess.networkToStrokeTriangles !== "function"
    || typeof tess.triangulateEvenOddContours !== "function"
    || typeof tess.sampleEdgeBezier !== "function"
    || typeof tess.adaptiveCurveSteps !== "function"
  ) {
    wasmMeshEnabled = false;
    resetMeshKernelBackendForTests();
    resetCurveKernelBackendForTests();
    resetRegionContourBackendForTests();
    return false;
  }
  setMeshKernelBackend(wasmMeshBackend(tess));
  setCurveKernelBackend(wasmCurveBackend(tess));
  if (typeof tess.sampleNetworkRegionContoursPacked === "function") {
    setRegionContourBackend(wasmRegionBackend(tess));
  }
  wasmMeshEnabled = true;
  return true;
}

let preparePromise: Promise<boolean> | undefined;

/**
 * Idempotent prepare used by editor boot, exporters, and the visual harness.
 * Safe to call many times; concurrent callers share one load attempt.
 */
export function prepareWasmMeshKernels(): Promise<boolean> {
  if (wasmMeshEnabled) {
    return Promise.resolve(true);
  }
  if (!preparePromise) {
    preparePromise = enableWasmMeshKernels().finally(() => {
      // Allow retry after a failed load (e.g. pkg built mid-session in dev).
      if (!wasmMeshEnabled) {
        preparePromise = undefined;
      }
    });
  }
  return preparePromise;
}

/** @deprecated Prefer {@link enableWasmMeshKernels}. */
export async function enableWasmStrokeKernels(options?: { force?: boolean }): Promise<boolean> {
  return enableWasmMeshKernels(options);
}

export function disableWasmMeshKernels(): void {
  wasmMeshEnabled = false;
  preparePromise = undefined;
  resetMeshKernelBackendForTests();
  resetCurveKernelBackendForTests();
  resetRegionContourBackendForTests();
}

/** @deprecated Prefer {@link disableWasmMeshKernels}. */
export function disableWasmStrokeKernels(): void {
  disableWasmMeshKernels();
}

export function wasmMeshKernelsEnabled(): boolean {
  return wasmMeshEnabled;
}

/** @deprecated Prefer {@link wasmMeshKernelsEnabled}. */
export function wasmStrokeKernelsEnabled(): boolean {
  return wasmMeshEnabled;
}

export function resetCurvaTessWasmForTests(): void {
  loadPromise = undefined;
  preparePromise = undefined;
  disableWasmMeshKernels();
}

/** Bench helper: respect `CURVA_WASM_STROKES=0` / `CURVA_WASM_MESH=0` opt-out. */
export async function maybeEnableWasmStrokeKernelsFromEnv(): Promise<boolean> {
  return prepareWasmMeshKernels();
}
